import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import { PrismAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { resilientFetch } from '../../utils/resilientFetch';
import { getPathConfig, buildQuickDirs, loadPathConfig } from '../../utils/paths';
import { validateApiResponse } from '../../lib/validateApiResponse';
import { useAuth } from '../../contexts/AuthContext';
import { elternOrdner, merkeStandardOrdner } from '../../lib/dateiStandardOrdner';
import { HtmlEditor } from './HtmlEditor';
import { PdfViewer } from './PdfViewer';

// Initialize mermaid once with dark theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  themeVariables: {
    primaryColor: '#1e293b',
    primaryTextColor: '#c0caf5',
    primaryBorderColor: '#334155',
    lineColor: '#7aa2f7',
    secondaryColor: '#0f172a',
    tertiaryColor: '#1a1b26',
    fontSize: '14px',
  },
  securityLevel: 'loose',
});

/** Whitelist editierbarer File-Extensions (muss zu Backend SAVABLE_EXTENSIONS passen) */
const SAVABLE_EXTENSIONS = new Set([
  '.html', '.htm', '.md', '.txt', '.json', '.yaml', '.yml',
  '.css', '.js', '.ts', '.tsx', '.jsx', '.py', '.sh',
  '.csv', '.mmd', '.merm', '.toml', '.cfg', '.ini', '.env', '.log',
  '.xml', '.svg',
]);

/** Renders Mermaid source to SVG with source/rendered toggle */
function MermaidRenderer({ source }: { source: string }) {
  const [view, setView] = useState<'rendered' | 'source'>('rendered');
  const [svgHtml, setSvgHtml] = useState<string>('');
  const [renderError, setRenderError] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function render() {
      try {
        const id = `mermaid-${Date.now()}`;
        const { svg } = await mermaid.render(id, source);
        if (!cancelled) {
          setSvgHtml(svg);
          setRenderError('');
        }
      } catch (err: any) {
        if (!cancelled) {
          setRenderError(err.message || 'Mermaid render error');
          setSvgHtml('');
        }
      }
    }
    render();
    return () => { cancelled = true; };
  }, [source]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toggle Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 12px', borderBottom: '1px solid var(--tn-border)',
        background: 'var(--tn-bg)', flexShrink: 0,
      }}>
        <button
          data-ai-id="file-preview-mermaid-view-rendered"
          onClick={() => setView('rendered')}
          style={{
            padding: '2px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
            background: view === 'rendered' ? 'var(--tn-bg-highlight)' : 'transparent',
            border: view === 'rendered' ? '1px solid var(--tn-border)' : '1px solid transparent',
            color: view === 'rendered' ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
            fontWeight: view === 'rendered' ? 600 : 400,
          }}
        >
          Rendered
        </button>
        <button
          data-ai-id="file-preview-mermaid-view-source"
          onClick={() => setView('source')}
          style={{
            padding: '2px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
            background: view === 'source' ? 'var(--tn-bg-highlight)' : 'transparent',
            border: view === 'source' ? '1px solid var(--tn-border)' : '1px solid transparent',
            color: view === 'source' ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
            fontWeight: view === 'source' ? 600 : 400,
          }}
        >
          Source
        </button>
        {renderError && (
          <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--tn-red)' }}>
            Syntax Error
          </span>
        )}
      </div>

      {/* Content */}
      {view === 'source' || (renderError && view === 'rendered') ? (
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {renderError && view === 'rendered' && (
            <div style={{
              padding: '8px 12px', fontSize: 11, color: 'var(--tn-red)',
              background: 'rgba(255,0,0,0.05)', borderBottom: '1px solid var(--tn-border)',
            }}>
              {renderError}
            </div>
          )}
          <pre style={{
            flex: 1, overflow: 'auto', padding: 12, fontSize: 12,
            color: 'var(--tn-text-subtle)', fontFamily: "'JetBrains Mono', monospace",
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
          }}>
            {source}
          </pre>
        </div>
      ) : (
        <div
          ref={containerRef}
          style={{
            flex: 1, overflow: 'auto', padding: 20,
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          }}
          dangerouslySetInnerHTML={{ __html: svgHtml }}
        />
      )}
    </div>
  );
}

/**
 * Map von Datei-Extension → Prism-Sprachname für Syntax-Highlighting.
 * Unbekannte Extensions fallen auf 'text' (kein Highlighting, aber saubere
 * Monospace-Darstellung mit Zeilennummern + horizontalem Scroll).
 */
const EXT_TO_LANG: Record<string, string> = {
  py: 'python', pyw: 'python',
  js: 'javascript', mjs: 'javascript', cjs: 'javascript',
  jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
  sh: 'bash', bash: 'bash', zsh: 'bash', env: 'bash',
  json: 'json', json5: 'json',
  yaml: 'yaml', yml: 'yaml', toml: 'toml',
  ini: 'ini', cfg: 'ini', conf: 'ini',
  css: 'css', scss: 'scss', sass: 'sass', less: 'less',
  html: 'markup', htm: 'markup', xml: 'markup', svg: 'markup', vue: 'markup',
  sql: 'sql', graphql: 'graphql', gql: 'graphql',
  go: 'go', rs: 'rust', rb: 'ruby', php: 'php',
  java: 'java', kt: 'kotlin', kts: 'kotlin', swift: 'swift', dart: 'dart',
  c: 'c', h: 'c', cpp: 'cpp', cc: 'cpp', hpp: 'cpp', cs: 'csharp',
  md: 'markdown', markdown: 'markdown',
  dockerfile: 'docker', makefile: 'makefile',
  csv: 'csv', tsv: 'csv', log: 'text', txt: 'text',
};

function extToLang(ext: string): string {
  const e = ext.replace(/^\./, '').toLowerCase();
  return EXT_TO_LANG[e] || 'text';
}

const CODE_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
// Über dieser Größe kein Syntax-Highlighting (Prism tokenisiert die ganze Datei —
// bei riesigen/minifizierten Files würde das den Renderer blockieren).
const HIGHLIGHT_MAX_BYTES = 400 * 1024;
const HIGHLIGHT_MAX_LINES = 8000;

/**
 * Syntax-hervorgehobener Code-Viewer für alle textbasierten Dateitypen.
 * - Sprache aus Extension abgeleitet (Prism, ~200 Sprachen via async-load)
 * - Zeilennummern, oneDark-Theme
 * - Umbruch standardmäßig AUS (horizontaler Scroll = korrektes Code-Layout);
 *   per Button umschaltbar
 * - Schriftgröße folgt dem globalen fontSize; Ctrl/Cmd+Mausrad zoomt
 * - Große/minifizierte Dateien fallen auf schlichtes <pre> zurück (fail-safe
 *   gegen Renderer-Blockade)
 */
function CodeViewer({ content, ext, fontSize, onZoom }: {
  content: string;
  ext: string;
  fontSize: number;
  onZoom: (delta: number) => void;
}) {
  const [wrap, setWrap] = useState(false);
  const lang = extToLang(ext);
  const tooLarge = content.length > HIGHLIGHT_MAX_BYTES
    || content.split('\n', HIGHLIGHT_MAX_LINES + 1).length > HIGHLIGHT_MAX_LINES;

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      onZoom(e.deltaY < 0 ? 1 : -1);
    }
  };

  const header = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '3px 12px', borderBottom: '1px solid var(--tn-border)',
      background: 'var(--tn-bg)', flexShrink: 0, fontSize: 10,
      color: 'var(--tn-text-muted)',
    }}>
      <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {tooLarge ? 'text (groß)' : lang}
      </span>
      <span style={{ marginLeft: 'auto', opacity: 0.7 }}>Ctrl+Rad = Zoom</span>
      <button
        data-ai-id="file-preview-code-wrap-toggle"
        onClick={() => setWrap(w => !w)}
        style={{
          padding: '1px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
          background: wrap ? 'var(--tn-bg-highlight)' : 'transparent',
          border: '1px solid var(--tn-border)',
          color: wrap ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
          fontWeight: wrap ? 600 : 400,
        }}
        title="Zeilenumbruch an/aus"
      >
        Umbruch {wrap ? 'an' : 'aus'}
      </button>
    </div>
  );

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      onWheel={handleWheel}
    >
      {header}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, background: 'var(--tn-bg-dark)' }}>
        {tooLarge ? (
          <pre style={{
            margin: 0, padding: '12px 16px', fontSize,
            color: 'var(--tn-text-subtle)', fontFamily: CODE_MONO,
            whiteSpace: wrap ? 'pre-wrap' : 'pre',
            wordBreak: wrap ? 'break-word' : 'normal',
          }}>
            {content}
          </pre>
        ) : (
          <SyntaxHighlighter
            language={lang}
            style={oneDark}
            showLineNumbers
            wrapLongLines={wrap}
            customStyle={{
              margin: 0, padding: '12px 16px', background: 'transparent',
              fontSize, minHeight: '100%',
            }}
            codeTagProps={{ style: { fontFamily: CODE_MONO, fontSize } }}
            lineNumberStyle={{
              color: 'var(--tn-text-muted)', opacity: 0.4,
              minWidth: '2.5em', userSelect: 'none',
            }}
          >
            {content}
          </SyntaxHighlighter>
        )}
      </div>
    </div>
  );
}

interface FileEntry {
  name: string;
  path: string;
  isDir: boolean;
  ext?: string | null;
}

interface FileContent {
  path: string;
  content: string;
  mimeType: string;
  ext?: string;
}

interface ClaudeMapRef {
  label: string;
  path: string;
  type: string; // 'ref' | 'link' | 'doc' | 'table'
}

interface ClaudeMapItem {
  path: string;
  label: string;
  refs: ClaudeMapRef[];
}

interface ClaudeMapGroup {
  key: string;
  label: string;
  items: ClaudeMapItem[];
}

interface FilePreviewProps {
  watchPath?: string;
  /**
   * Eine DATEI, die beim Aufbau direkt geoeffnet wird (nicht nur der Ordner).
   *
   * Die Guide-Spalte richtet den Arbeitsbereich fuer EINE Frage her; ein Panel, das
   * nur den Ordner zeigt, laesst Rafael die Datei selbst suchen — genau die Arbeit,
   * die ihm die Vorbereitung abnehmen soll. Fuer registrierte Business-Dokumente
   * gibt es business-docs; fuer jede andere Datei ist das hier der Weg.
   */
  initialFile?: string;
  stageDir?: string;
  /**
   * Schreibt den aktuell offenen Ordner als neuen Start-Ordner in die Tab-Config
   * (analog BrowserPanel.onUrlChange). Nur gesetzt, wo die Config auch persistiert
   * werden kann — im LayoutManager. Fehlt sie (ToolHub), blendet das
   * Panel den "Als Standard"-Button aus, statt einen toten Knopf anzubieten.
   */
  onWatchPathChange?: (path: string) => void;
  /**
   * Arbeitsbereich, unter dem "Als Standard" den Ordner zusaetzlich pro Mensch
   * merkt (lib/dateiStandardOrdner.ts) — damit auch ein NEU geoeffneter Reiter
   * dort startet, nicht nur dieser eine. Ohne Schluessel bleibt es beim Tab.
   */
  standardKey?: string;
  /**
   * Haus der Buehne, die diese Kachel traegt (GuidePanel setzt es beim Herrichten). Der Pfad
   * wird dann dort aufgeloest, nicht im Haus des Fensters (`/api/file?haus=`, Server reicht
   * weiter: lib/datei-im-haus.ts). Ohne Angabe: eigenes Haus wie bisher.
   */
  haus?: 'dev' | 'partner';
}

/** Server-Fehlertext lesbar machen: `{"error":"…"}` → `…`. */
function fehlerText(roh: string, status: number): string {
  try { const e = (JSON.parse(roh) as { error?: unknown }).error; if (typeof e === 'string' && e) return e; } catch { /* kein JSON */ }
  return roh.trim() ? roh.trim().slice(0, 300) : `HTTP ${status}`;
}

const API = '/api';

// Quick navigation shortcuts (loaded from server config)
const QUICK_DIRS: Array<{ label: string; path: string; color?: string }> = buildQuickDirs(getPathConfig());

/**
 * Übersetzt Sandbox-Pfade des Rafael-/Business-/Privat-Assistenten in echte Host-Pfade.
 *
 * Der Assistent läuft in einem Docker-Sandbox-Container, in den die Workspaces unter
 * `/work/sources/` gemountet werden (siehe packages/agent-sandbox/adapters/*-daemon.mjs).
 * Er gibt deshalb Pfade wie `/work/sources/business/sales/proposals/X.html` aus — die
 * existieren nur im Container, nicht auf dem Host. Das File-Preview-Panel läuft aber auf
 * dem Host und validiert gegen echte Pfade. Ohne diese Übersetzung scheitert jeder
 * eingefügte Assistent-Pfad mit "path outside allowed directories".
 *
 * Mapping ist deterministisch (aus den seedWorkdir-Adaptern):
 *   /work/sources/business/…         → <businessDir>/…
 *   /work/sources/privat/…           → /root/orchestrator/workspaces/privat/…
 *   /work/sources/arbeits-tagebuch/… → /root/projekte/local-storage/diary/…
 */
function toHostPath(p: string): string {
  const trimmed = p.trim();
  const businessDir = getPathConfig().businessDir;
  const SANDBOX_MAP: Array<[RegExp, string]> = [
    [/^\/work\/sources\/business(?=\/|$)/, businessDir],
    [/^\/work\/sources\/privat(?=\/|$)/, '/root/orchestrator/workspaces/privat'],
    [/^\/work\/sources\/arbeits-tagebuch(?=\/|$)/, '/root/projekte/local-storage/diary'],
  ];
  for (const [pattern, hostBase] of SANDBOX_MAP) {
    if (pattern.test(trimmed)) return trimmed.replace(pattern, hostBase);
  }
  return trimmed;
}

export default function FilePreview({ watchPath, stageDir, onWatchPathChange, initialFile, standardKey, haus }: FilePreviewProps) {
  const hausRef = useRef(haus);
  hausRef.current = haus;
  const hausQ = () => (hausRef.current ? `&haus=${hausRef.current}` : '');
  /*
   * DIE VORGEGEBENE DATEI FEHLT — SICHTBAR, NICHT „Datei auswaehlen" (25.10.2026, Engelmann-Buehne).
   * Bisher landete der Fehler in `error`, und das erfolgreiche Listen des Start-Ordners loeschte ihn
   * gleich wieder (`loadDir` setzt `error` zurueck). Die Kachel sah dann aus wie ein leerer
   * Datei-Browser, waehrend die Buehne Erfolg meldete. Dieser Fehler gehoert der Vorgabe und
   * verschwindet nur, wenn eine Datei wirklich geladen ist.
   */
  const [vorgabeFehler, setVorgabeFehler] = useState<string | null>(null);
  // Per-Row-Download im Baum ist gated: sichtbar für admin/product-owner, und
  // (analog ToolHub.tsx) auch wenn Auth ganz aus ist — der Dev-Server läuft ohne
  // users.json, dort ist `user` dauerhaft null und das ist Rafaels eigene Instanz.
  const { user } = useAuth();
  const isAdmin = !user || user.role === 'admin' || user.role === 'product-owner';
  /*
   * STARTORDNER IM HAUS DER BUEHNE (25.10.2026): traegt die Kachel ein Haus und eine Datei, ist
   * `watchPath` der Arbeitsbereich des FENSTERhauses (LayoutManager: activeDir/workDir) — auf
   * dem anderen Haus gibt es ihn nicht (Partner-`general` liegt woanders als Dev-`general`), die
   * Auflistung beim Einhaengen lief dort auf 404. Dann beginnt der Baum im Ordner der Datei.
   */
  const startOrdner = haus && initialFile ? (initialFile.replace(/\/[^/]+$/, '') || '/') : (watchPath ?? '');
  const [currentDir, setCurrentDir] = useState(startOrdner);
  // Zuletzt als Standard gesetzter Ordner — lokal mitgeführt, damit der Button
  // sofort reagiert, ohne auf den Layout-Save-Roundtrip zu warten.
  const [defaultDir, setDefaultDir] = useState(watchPath ?? '');
  const [defaultSaved, setDefaultSaved] = useState(false);
  const kannStandardMerken = !!onWatchPathChange || !!standardKey;
  // Besuchte Ordner fuer den Zurueck-Knopf (Sahori 11.09.: "a button to 'go back'").
  const verlaufRef = useRef<string[]>([]);
  const currentDirRef = useRef(currentDir);
  currentDirRef.current = currentDir;
  const [verlaufLaenge, setVerlaufLaenge] = useState(0);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileContent | null>(null);
  const [error, setError] = useState('');
  const [inputPath, setInputPath] = useState(startOrdner);
  const [stageLoading, setStageLoading] = useState(false);
  const [stageSuccess, setStageSuccess] = useState(false);

  // Markdown render mode: 'preview' = ReactMarkdown (dark), 'html' = WerkING branded HTML (light).
  // Standard ist WerkING (Rafael, 20.10.2026); Dark bleibt per Umschalter waehlbar.
  const [mdView, setMdView] = useState<'preview' | 'html'>('html');

  // Persistent font size (stored in localStorage)
  const [fontSize, setFontSize] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('cui-filepreview-fontsize') || '14', 10); } catch { return 14; }
  });
  const changeFontSize = (delta: number) => {
    setFontSize(prev => {
      const next = Math.max(9, Math.min(28, prev + delta));
      try { localStorage.setItem('cui-filepreview-fontsize', String(next)); } catch {} // silent-ok: localStorage may be disabled
      return next;
    });
  };
  const [mdHtml, setMdHtml] = useState<string>('');
  const [mdHtmlLoading, setMdHtmlLoading] = useState(false);
  /* Geschwister-Stylesheets einer HTML-Vorschau, INHALTLICH (siehe .html-Zweig unten).
   * geladenFuer merkt sich, zu welcher Datei sie gehoeren — sonst zeigt die naechste
   * Seite kurz das Blatt der vorigen. */
  const [htmlStile, setHtmlStile] = useState<{ geladenFuer: string; css: string } | null>(null);
  const [htmlStileLaden, setHtmlStileLaden] = useState(false);

  // Edit mode state — null = nicht im Edit-Mode, string = Editor offen mit diesem Inhalt
  const [editingContent, setEditingContent] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  // Edit-Modus-View: 'source' = textarea, 'visual' = TinyMCE (nur bei .html)
  const [editView, setEditView] = useState<'source' | 'visual'>('source');

  // CLAUDE mode state
  const [mode, setMode] = useState<'browse' | 'claude'>('browse');
  const [claudeMap, setClaudeMap] = useState<ClaudeMapGroup[]>([]);
  const [claudeTotal, setClaudeTotal] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());
  const [claudeLoading, setClaudeLoading] = useState(false);

  const loadDir = useCallback(async (rawDirPath: string, opts?: { ohneVerlauf?: boolean }) => {
    if (window.__cuiServerAlive === false) return;
    const dirPath = toHostPath(rawDirPath);
    const vorher = currentDirRef.current;
    try {
      const res = await resilientFetch(`${API}/files?path=${encodeURIComponent(dirPath)}${hausQ()}`);
      if (!res.ok) throw new Error(await res.text());
      const raw = await res.json();
      const data = validateApiResponse<{ entries: FileEntry[]; path: string }>(raw, '/api/files', {
        entries: 'array',
        path: 'string',
      });
      setEntries(data.entries);
      if (!opts?.ohneVerlauf && vorher && vorher !== data.path) {
        verlaufRef.current = [...verlaufRef.current, vorher].slice(-50);
        setVerlaufLaenge(verlaufRef.current.length);
      }
      currentDirRef.current = data.path;
      setCurrentDir(data.path);
      setError('');
    } catch (err: any) {
      console.warn('[FilePreview] loadDir:', err);
      setError(err.message);
    }
  }, []);

  const loadFile = useCallback(async (rawFilePath: string, opts?: { vorgabe?: boolean }) => {
    if (window.__cuiServerAlive === false) return;
    const filePath = toHostPath(rawFilePath);
    const wo = hausRef.current ? ` im Haus ${hausRef.current === 'partner' ? 'Partner' : 'Dev'}` : '';
    const vorgabeGescheitert = (grund: string) => {
      setSelectedFile(null);
      setVorgabeFehler(`Vorgegebene Datei nicht ladbar${wo}: ${filePath} — ${grund}`);
    };
    /* Binaerdateien bindet die Kachel direkt als Adresse ein; ob es sie gibt, sagt erst eine
     * Kopfanfrage. Nur fuer die Vorgabe — ein Klick im Baum zeigt ohnehin, was da ist. */
    const binaerDa = async (url: string): Promise<boolean> => {
      if (!opts?.vorgabe) return true;
      try {
        const r = await resilientFetch(url, { method: 'HEAD' });
        if (r.ok) return true;
        // Der Kopf traegt keinen Grund; die GET-Antwort des Fehlers ist klein (JSON mit Haus und Pfad).
        const x = await resilientFetch(url);
        vorgabeGescheitert(fehlerText(await x.text(), x.status));
      } catch (e) { vorgabeGescheitert(e instanceof Error ? e.message : String(e)); }
      return false;
    };
    // Reset rendered HTML cache when loading a new file
    setMdHtml('');
    setMdView('html');
    setHtmlStile(null);
    // Reset Edit-Mode beim File-Wechsel
    setEditingContent(null);
    setEditError('');
    setEditView('source');
    const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
    const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'];
    const videoExts = ['mp4', 'webm', 'mov', 'm4v', 'ogv'];
    const audioExts = ['mp3', 'wav', 'm4a', 'ogg', 'flac'];
    const pdfExts = ['pdf'];

    const binaerUrl = `${API}/file?path=${encodeURIComponent(filePath)}${hausQ()}`;
    if ([...imageExts, ...videoExts, ...audioExts, ...pdfExts].includes(ext)) {
      if (!(await binaerDa(binaerUrl))) return;
      setVorgabeFehler(null);
    }

    if (imageExts.includes(ext)) {
      setSelectedFile({
        path: filePath,
        content: `${API}/file?path=${encodeURIComponent(filePath)}${hausQ()}`,
        mimeType: 'image',
        ext: `.${ext}`,
      });
      return;
    }

    // Video/Audio: direkte Binary-URL, Player rendert client-seitig (Range-Streaming via sendFile)
    if (videoExts.includes(ext) || audioExts.includes(ext)) {
      setSelectedFile({
        path: filePath,
        content: `${API}/file?path=${encodeURIComponent(filePath)}${hausQ()}`,
        mimeType: videoExts.includes(ext) ? 'video' : 'audio',
        ext: `.${ext}`,
      });
      return;
    }

    if (pdfExts.includes(ext)) {
      setSelectedFile({
        path: filePath,
        content: `${API}/file?path=${encodeURIComponent(filePath)}${hausQ()}`,
        mimeType: 'application/pdf',
        ext: `.${ext}`,
      });
      return;
    }

    try {
      const res = await resilientFetch(`${API}/file?path=${encodeURIComponent(filePath)}${hausQ()}`);
      if (!res.ok) throw new Error(fehlerText(await res.text(), res.status));
      const raw = await res.json();
      const data = validateApiResponse<FileContent>(raw, '/api/file', {
        path: 'string',
        content: 'string',
        mimeType: 'string',
        ext: { type: 'string', optional: true },
      });
      setSelectedFile(data);
      setVorgabeFehler(null);
    } catch (err: any) {
      console.warn('[FilePreview] loadFile:', err);
      if (opts?.vorgabe) vorgabeGescheitert(err.message);
      else setError(err.message);
    }
  }, []);

  /**
   * Vorgegebene Datei oeffnen — einmal je Pfad. Der Ordner daneben wird auf das
   * Verzeichnis der Datei gesetzt, damit die Liste links zum geoeffneten Text passt
   * und er von dort weiterklicken kann.
   */
  const vorgabeGeladen = useRef<string | null>(null);

  /*
   * BAUM ODER TEXT — wer die Datei schon benennt, will nicht suchen.
   *
   * Der Dateibaum ist fix 240-260px breit. Das ist richtig, solange die Kachel ein
   * Datei-Browser ist. Gibt ein Guide-Rezept aber `initialFile` vor, ist die Kachel
   * kein Browser mehr, sondern EINE Seite — und in einer 55%-Kachel frisst der Baum
   * rund 40% der Breite, waehrend der Entscheidungstext in einer schmalen Spalte
   * daneben steht (Coach am 06.09. an den Fenstern von k15 und k-gemini-tarif).
   *
   * Der Baum wird deshalb nicht entfernt, sondern eingeklappt: er bleibt einen Klick
   * entfernt, gibt aber die Lesflaeche frei, um die es geht.
   */
  const [baumOffen, setBaumOffen] = useState(!initialFile);
  useEffect(() => {
    if (!initialFile || vorgabeGeladen.current === initialFile) return;
    vorgabeGeladen.current = initialFile;
    const ordner = initialFile.replace(/\/[^/]+$/, '');
    if (ordner) { setCurrentDir(ordner); setInputPath(ordner); }
    void loadFile(initialFile, { vorgabe: true });
  }, [initialFile, loadFile]);

  const loadClaudeMap = useCallback(async () => {
    if (window.__cuiServerAlive === false) return;
    setClaudeLoading(true);
    try {
      const res = await resilientFetch(`${API}/claude-map`);
      if (!res.ok) throw new Error(await res.text());
      const raw = await res.json();
      const data = validateApiResponse<{ groups: ClaudeMapGroup[]; total: number }>(raw, '/api/claude-map', {
        groups: 'array',
        total: 'number',
      });
      setClaudeMap(data.groups);
      setClaudeTotal(data.total);
      // Expand first group by default
      if (data.groups.length > 0) {
        setExpandedGroups(new Set(data.groups.map((g: ClaudeMapGroup) => g.key)));
      }
    } catch (err: any) {
      console.warn('[FilePreview] loadClaudeMap:', err);
      setError(err.message);
    } finally {
      setClaudeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentDir) loadDir(currentDir);
    const onReconnect = () => { if (currentDir) loadDir(currentDir); };
    window.addEventListener('cui-reconnected', onReconnect);
    return () => window.removeEventListener('cui-reconnected', onReconnect);
  }, []);

  // Load claude map when switching to claude mode
  useEffect(() => {
    if (mode === 'claude' && claudeMap.length === 0) {
      loadClaudeMap();
    }
  }, [mode]);

  function navigate() {
    const raw = inputPath.trim();
    if (!raw) return;
    // Sandbox-Pfade des Assistenten (/work/sources/…) auf echte Host-Pfade übersetzen.
    const path = toHostPath(raw);
    if (path !== raw) setInputPath(path); // Adressleiste zeigt den echten Pfad
    // Der Assistent gibt Datei-Pfade aus (…/X.html). Ein Datei-Pfad öffnet die Vorschau
    // direkt und listet den übergeordneten Ordner; ein Verzeichnis-Pfad wird gelistet.
    const lastSegment = path.split('/').pop() ?? '';
    const looksLikeFile = /\.[a-z0-9]{1,8}$/i.test(lastSegment);
    if (looksLikeFile) {
      const parent = path.replace(/\/[^/]+\/?$/, '') || '/';
      loadDir(parent);
      loadFile(path);
    } else {
      loadDir(path);
      setSelectedFile(null);
    }
  }

  // Merkt den aktuell offenen Ordner als Start-Ordner des Panels. Bewusst nur auf
  // Klick, nicht bei jeder Navigation: ein Auto-Persist bei jedem Ordnerwechsel
  // löst pro Klick eine Layout-Save-Welle aus (siehe LayoutManager, Echo-Loop).
  function pinCurrentDirAsDefault() {
    if (!kannStandardMerken || !currentDir) return;
    onWatchPathChange?.(currentDir);
    if (standardKey) merkeStandardOrdner(user?.id, standardKey, currentDir);
    setDefaultDir(currentDir);
    setDefaultSaved(true);
    setTimeout(() => setDefaultSaved(false), 2000);
  }

  function goUp() {
    const parent = elternOrdner(currentDir);
    setInputPath(parent);
    loadDir(parent);
    setSelectedFile(null);
  }

  function goBack() {
    const ziel = verlaufRef.current[verlaufRef.current.length - 1];
    if (!ziel) return;
    verlaufRef.current = verlaufRef.current.slice(0, -1);
    setVerlaufLaenge(verlaufRef.current.length);
    setInputPath(ziel);
    loadDir(ziel, { ohneVerlauf: true });
    setSelectedFile(null);
  }

  function goToDefault() {
    if (!defaultDir || defaultDir === currentDir) return;
    setInputPath(defaultDir);
    loadDir(defaultDir);
    setSelectedFile(null);
  }

  async function stageFile() {
    if (window.__cuiServerAlive === false) return;
    if (!selectedFile || !stageDir) return;
    setStageLoading(true);
    setStageSuccess(false);
    setError('');

    try {
      const res = await resilientFetch(`${API}/files/move?${hausQ().slice(1)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourcePath: selectedFile.path,
          targetDir: stageDir,
          operation: 'copy',
        }),
        signal: AbortSignal.timeout(25000),
      }, 1); // ein Versuch: Kopieren wird nicht still wiederholt

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const data = await res.json();
      setStageSuccess(true);
      console.log('[FilePreview] Staged file:', data);
      setTimeout(() => setStageSuccess(false), 3000);
    } catch (err: any) {
      console.warn('[FilePreview] stageFile:', err);
      setError(`Stage failed: ${err.message}`);
    } finally {
      setStageLoading(false);
    }
  }

  function toggleGroup(key: string) {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function toggleRefs(path: string) {
    setExpandedRefs(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }

  // Resolve a relative ref path to absolute
  // Type icons for different ref types
  function refTypeIcon(type: string): string {
    switch (type) {
      case 'ref': return '⚙'; // refs/*.md config files
      case 'link': return '→'; // [text](path) links
      case 'doc': return '📄'; // backtick docs
      case 'table': return '📋'; // table-referenced files
      default: return '→';
    }
  }

  function renderClaudeTree() {
    if (claudeLoading) {
      return (
        <div style={{ padding: 12, fontSize: 11, color: 'var(--tn-text-muted)', textAlign: 'center' }}>
          Scanne CLAUDE.md Files...
        </div>
      );
    }

    return (
      <div style={{ overflow: 'auto', flex: 1 }}>
        {claudeMap.map(group => (
          <div key={group.key}>
            {/* Group Header */}
            <button
              data-ai-id={`file-preview-claude-group-${group.key}`}
              onClick={() => toggleGroup(group.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                padding: '4px 8px', background: 'var(--tn-bg-dark)', border: 'none',
                borderBottom: '1px solid var(--tn-border)',
                color: 'var(--tn-text)', fontSize: 10, fontWeight: 700,
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 8, opacity: 0.6 }}>{expandedGroups.has(group.key) ? '▼' : '▶'}</span>
              <span>{group.label}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--tn-text-muted)', fontWeight: 400 }}>{group.items.length}</span>
            </button>

            {/* Group Items */}
            {expandedGroups.has(group.key) && group.items.map(item => (
              <div key={item.path}>
                {/* CLAUDE.md Item */}
                <button
                  data-ai-id={`file-preview-claude-item-${item.path}`}
                  onClick={() => loadFile(item.path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                    padding: '3px 8px 3px 16px',
                    background: selectedFile?.path === item.path ? 'var(--tn-bg-highlight)' : 'transparent',
                    border: 'none',
                    color: selectedFile?.path === item.path ? 'var(--tn-purple)' : 'var(--tn-text-subtle)',
                    fontSize: 11, textAlign: 'left', cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 9, color: 'var(--tn-purple)', fontWeight: 700, flexShrink: 0 }}>C</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                  {item.refs.length > 0 && (
                    <span
                      data-ai-id={`file-preview-claude-refs-toggle-${item.path}`}
                      onClick={(e) => { e.stopPropagation(); toggleRefs(item.path); }}
                      style={{
                        marginLeft: 'auto', fontSize: 9, color: 'var(--tn-text-muted)',
                        padding: '0 4px', cursor: 'pointer', flexShrink: 0,
                      }}
                      title={`${item.refs.length} Referenzen`}
                    >
                      {expandedRefs.has(item.path) ? '−' : '+'}{item.refs.length}
                    </span>
                  )}
                </button>

                {/* Refs sub-items */}
                {expandedRefs.has(item.path) && item.refs.map(ref => (
                    <button
                      key={ref.path}
                      data-ai-id={`file-preview-claude-ref-${ref.path}`}
                      onClick={() => loadFile(ref.path)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                        padding: '2px 8px 2px 28px',
                        background: selectedFile?.path === ref.path ? 'var(--tn-bg-highlight)' : 'transparent',
                        border: 'none',
                        color: 'var(--tn-text-muted)', fontSize: 10, textAlign: 'left', cursor: 'pointer',
                      }}
                      title={`${ref.type}: ${ref.path}`}
                    >
                      <span style={{ fontSize: 8, opacity: 0.6, flexShrink: 0 }}>{refTypeIcon(ref.type)}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.label}</span>
                    </button>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  function renderContent() {
    if (!selectedFile && vorgabeFehler) {
      return (
        <div role="alert" data-ai-id="file-preview-vorgabe-fehlt"
          style={{ margin: 16, padding: '12px 14px', border: '1px solid var(--tn-red)', borderRadius: 6,
            color: 'var(--tn-red)', background: 'var(--tn-bg-dark)', fontSize: 13, lineHeight: 1.5, wordBreak: 'break-all' }}>
          {vorgabeFehler}
        </div>
      );
    }
    if (!selectedFile) {
      return (
        <div style={{ padding: 20, color: 'var(--tn-text-muted)', fontSize: 12, textAlign: 'center' }}>
          {mode === 'claude' ? 'CLAUDE.md auswaehlen' : 'Datei auswaehlen'}
        </div>
      );
    }

    const content = selectedFile.content;
    const mimeType = selectedFile.mimeType;
    const ext = selectedFile.ext ?? '';

    // Edit-Mode: Source-Editor (textarea) oder Visual-Editor (TinyMCE bei HTML)
    if (editingContent !== null && SAVABLE_EXTENSIONS.has(ext.toLowerCase())) {
      const dirty = editingContent !== content;
      const sizeKb = (new Blob([editingContent]).size / 1024).toFixed(1);
      const isHtml = ext === '.html' || ext === '.htm';
      const showVisual = isHtml && editView === 'visual';
      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {editError && (
            <div style={{
              padding: '6px 12px', fontSize: 11, color: 'var(--tn-red)',
              background: 'rgba(229,62,62,0.1)', borderBottom: '1px solid var(--tn-red)',
            }}>
              Fehler: {editError}
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px',
            fontSize: 10, color: 'var(--tn-text-muted)',
            background: 'var(--tn-bg)', borderBottom: '1px solid var(--tn-border)',
          }}>
            {/* View-Toggle nur bei HTML-Files */}
            {isHtml && (
              <>
                <button
                  data-ai-id="file-preview-edit-view-source"
                  onClick={() => setEditView('source')}
                  title="HTML-Source-Code direkt bearbeiten"
                  style={{
                    padding: '2px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                    background: editView === 'source' ? 'var(--tn-bg-highlight)' : 'transparent',
                    border: editView === 'source' ? '1px solid var(--tn-border)' : '1px solid transparent',
                    color: editView === 'source' ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                    fontWeight: editView === 'source' ? 600 : 400,
                  }}
                >
                  Source
                </button>
                <button
                  data-ai-id="file-preview-edit-view-visual"
                  onClick={() => setEditView('visual')}
                  title="WYSIWYG-Editor (TinyMCE)"
                  style={{
                    padding: '2px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                    background: editView === 'visual' ? 'var(--tn-bg-highlight)' : 'transparent',
                    border: editView === 'visual' ? '1px solid var(--tn-border)' : '1px solid transparent',
                    color: editView === 'visual' ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                    fontWeight: editView === 'visual' ? 600 : 400,
                  }}
                >
                  Visual
                </button>
                <span style={{ width: 1, height: 12, background: 'var(--tn-border)' }} />
              </>
            )}
            <span style={{ color: dirty ? 'var(--tn-orange)' : 'var(--tn-text-muted)' }}>
              {dirty ? '● Ungespeicherte Änderungen' : 'Keine Änderungen'}
            </span>
            <span style={{ marginLeft: 'auto' }}>{sizeKb} KB · {editingContent.length} Zeichen</span>
          </div>
          {showVisual ? (
            <HtmlEditor
              content={editingContent}
              onChange={(newContent) => setEditingContent(newContent)}
            />
          ) : (
            <textarea
              data-ai-id="file-preview-edit-textarea"
              value={editingContent}
              onChange={(e) => setEditingContent(e.target.value)}
              spellCheck={false}
              style={{
                flex: 1, width: '100%', resize: 'none',
                padding: '12px 16px', boxSizing: 'border-box',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                fontSize, lineHeight: 1.5,
                background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
                border: 'none', outline: 'none',
                tabSize: 2,
              }}
            />
          )}
        </div>
      );
    }

    if (mimeType === 'image') {
      return (
        <div style={{ padding: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <img src={content} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      );
    }

    if (mimeType === 'video') {
      return (
        <div style={{ padding: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, background: 'var(--tn-bg-dark)' }}>
          {/* key erzwingt Player-Reset beim File-Wechsel (src-Wechsel allein lädt nicht neu) */}
          <video key={content} src={content} controls preload="metadata" style={{ maxWidth: '100%', maxHeight: '100%', outline: 'none' }} />
        </div>
      );
    }

    if (mimeType === 'audio') {
      return (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <audio key={content} src={content} controls preload="metadata" style={{ width: '100%', maxWidth: 640 }} />
        </div>
      );
    }

    if (mimeType === 'application/pdf') {
      return <PdfViewer url={content} />;
    }

    if (ext === '.md' || ext === '.mdx') {
      const loadRenderedHtml = async () => {
        if (mdHtml) return; // already loaded for this file
        setMdHtmlLoading(true);
        try {
          const res = await resilientFetch(`${API}/file/render-md?path=${encodeURIComponent(selectedFile.path)}${hausQ()}`);
          if (!res.ok) throw new Error(await res.text());
          const html = await res.text();
          setMdHtml(html);
        } catch (err: any) {
          setMdHtml(`<html><body style="font-family:sans-serif;padding:40px;color:#e53e3e"><h2>Render Error</h2><pre>${err.message}</pre></body></html>`);
        } finally {
          setMdHtmlLoading(false);
        }
      };

      // Auto-load rendered HTML when view is 'html' (default)
      if (mdView === 'html' && !mdHtml && !mdHtmlLoading) {
        loadRenderedHtml();
      }

      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Toggle Bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 12px', borderBottom: '1px solid var(--tn-border)',
            background: 'var(--tn-bg)', flexShrink: 0,
          }}>
            <button
              data-ai-id="file-preview-md-view-dark"
              onClick={() => { setMdView('preview'); }}
              title="Dark Mode Markdown Rendering"
              style={{
                padding: '2px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                background: mdView === 'preview' ? 'var(--tn-bg-highlight)' : 'transparent',
                border: mdView === 'preview' ? '1px solid var(--tn-border)' : '1px solid transparent',
                color: mdView === 'preview' ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                fontWeight: mdView === 'preview' ? 600 : 400,
              }}
            >
              Dark
            </button>
            <button
              data-ai-id="file-preview-md-view-html"
              onClick={() => { setMdView('html'); loadRenderedHtml(); }}
              title="WerkING Report Style (heller Hintergrund)"
              style={{
                padding: '2px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                background: mdView === 'html' ? 'var(--tn-bg-highlight)' : 'transparent',
                border: mdView === 'html' ? '1px solid var(--tn-border)' : '1px solid transparent',
                color: mdView === 'html' ? 'var(--tnf-dec15e)' : 'var(--tn-text-muted)',
                fontWeight: mdView === 'html' ? 600 : 400,
              }}
            >
              WerkING
            </button>
            {mdHtmlLoading && (
              <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--tn-text-muted)' }}>Rendering…</span>
            )}
          </div>

          {/* Content */}
          {mdView === 'html' ? (
            <iframe
              data-ai-id="file-preview-md-iframe"
              srcDoc={mdHtml || '<html><body style="font-family:sans-serif;padding:40px;color:#718096;text-align:center"><p>Wird gerendert…</p></body></html>'}
              style={{ flex: 1, border: 'none', width: '100%', background: 'white' }}
              sandbox="allow-scripts allow-same-origin"
            />
          ) : (
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                padding: '20px 32px',
                fontSize,
                lineHeight: 1.7,
                color: 'var(--tn-text)',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
              className="markdown-preview"
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 style={{fontSize: '28px', fontWeight: '700', color: 'var(--tn-text)', marginTop: '32px', marginBottom: '16px', borderBottom: '2px solid var(--tn-border)', paddingBottom: '8px'}} {...props} />,
                  h2: ({node, ...props}) => <h2 style={{fontSize: '22px', fontWeight: '600', color: 'var(--tn-text)', marginTop: '24px', marginBottom: '12px'}} {...props} />,
                  h3: ({node, ...props}) => <h3 style={{fontSize: '18px', fontWeight: '600', color: 'var(--tn-blue)', marginTop: '20px', marginBottom: '10px'}} {...props} />,
                  p: ({node, ...props}) => <p style={{marginBottom: '14px', color: 'var(--tn-text-subtle)'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{marginLeft: '20px', marginBottom: '14px', listStyleType: 'disc'}} {...props} />,
                  ol: ({node, ...props}) => <ol style={{marginLeft: '20px', marginBottom: '14px'}} {...props} />,
                  li: ({node, ...props}) => <li style={{marginBottom: '6px', color: 'var(--tn-text-subtle)'}} {...props} />,
                  code: ({node, inline, ...props}: any) => inline
                    ? <code style={{background: 'var(--tn-bg-highlight)', padding: '2px 6px', borderRadius: '3px', fontSize: '13px', fontFamily: 'monospace', color: 'var(--tn-blue)'}} {...props} />
                    : <code style={{display: 'block', background: 'var(--tn-bg-dark)', padding: '12px', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', overflow: 'auto', marginBottom: '14px', border: '1px solid var(--tn-border)'}} {...props} />,
                  table: ({node, ...props}) => <div style={{overflowX: 'auto', marginBottom: '20px'}}><table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'auto'}} {...props} /></div>,
                  thead: ({node, ...props}) => <thead style={{background: 'var(--tn-bg-highlight)', borderBottom: '2px solid var(--tn-border)'}} {...props} />,
                  th: ({node, ...props}) => <th style={{padding: '10px 12px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--tn-border)'}} {...props} />,
                  td: ({node, ...props}) => <td style={{padding: '8px 12px', borderBottom: '1px solid var(--tn-border)', wordBreak: 'break-word', maxWidth: '300px'}} {...props} />,
                  blockquote: ({node, ...props}) => <blockquote style={{borderLeft: '4px solid var(--tn-blue)', paddingLeft: '16px', marginLeft: '0', marginBottom: '14px', color: 'var(--tn-text-muted)', fontStyle: 'italic'}} {...props} />,
                  hr: ({node, ...props}) => <hr style={{border: 'none', borderTop: '1px solid var(--tn-border)', margin: '24px 0'}} {...props} />,
                  a: ({node, href, ...props}) => {
                    if (mode === 'claude' && href && !href.startsWith('http') && selectedFile) {
                      let resolved = href;
                      if (href.startsWith('/')) {
                        resolved = href;
                      } else if (href.startsWith('refs/')) {
                        resolved = `${getPathConfig().claudeUserHome}/.claude/${href}`;
                      } else {
                        const dir = (selectedFile.path).replace(/\/[^/]+$/, '');
                        resolved = `${dir}/${href}`;
                      }
                      return (
                        <a
                          style={{color: 'var(--tn-purple)', textDecoration: 'underline', cursor: 'pointer'}}
                          onClick={(e) => { e.preventDefault(); loadFile(resolved); }}
                          title={resolved}
                          {...props}
                        />
                      );
                    }
                    return <a style={{color: 'var(--tn-blue)', textDecoration: 'none'}} href={href} {...props} />;
                  },
                  strong: ({node, ...props}) => <strong style={{fontWeight: '600', color: 'var(--tn-text)'}} {...props} />,
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      );
    }

    if (ext === '.mmd' || ext === '.merm') {
      return <MermaidRenderer source={content} />;
    }

    if (ext === '.html' || ext === '.htm') {
      const htmlDir = (selectedFile.path).replace(/\/[^/]+$/, '');

      /* GESCHWISTER-STYLESHEETS MUESSEN INLINE, NICHT VERLINKT (12.09.2026).
       *
       * Rafael: „so haessliche weisse Bloecke". Gemessen: jede Seite, die ihr Aussehen
       * aus einer Nachbardatei laedt (`<link rel=stylesheet href="stil.css">`), rendert
       * in DIESER Kachel weiss — 434 der Entscheidungsgrundlagen tun das.
       *
       * Grund: unten wird jede relative Adresse auf `${API}/file?path=...` umgeschrieben.
       * Dieser Endpunkt liefert die Datei in einem JSON-Umschlag und meldet
       * `Content-Type: application/json`. Ein Browser WEIGERT SICH, ein so geliefertes
       * Stylesheet anzuwenden (strenge MIME-Pruefung fuer <link rel=stylesheet>). Das
       * Blatt wird geladen und verworfen, die Seite bleibt ohne Hintergrund — und das
       * `background:'white'` des iframes schlaegt durch.
       *
       * Bilder trifft das nicht: die kommen als data:-URL bzw. werden vom Browser nach
       * Inhalt erkannt, nicht nach MIME-Typ. Nur Stylesheets sind streng.
       *
       * Deshalb: Geschwister-CSS wird VORHER geholt und sein INHALT in ein <style> im
       * srcDoc geschrieben. Nicht inline in die 434 Dateien kopiert — das eine
       * gemeinsame Blatt bleibt die eine Quelle.
       */
      const cssAdressen = Array.from(
        content.matchAll(/<link\b[^>]*rel\s*=\s*["']?stylesheet["']?[^>]*>/gi)
      ).map(m => {
        const href = /href\s*=\s*"([^"]+)"/i.exec(m[0])?.[1] ?? '';
        return { tag: m[0], href };
      }).filter(l => l.href && !/^(https?:)?\/\//.test(l.href) && !l.href.startsWith('data:'));

      if (cssAdressen.length > 0 && htmlStile?.geladenFuer !== selectedFile.path) {
        if (!htmlStileLaden) {
          setHtmlStileLaden(true);
          (async () => {
            const teile: string[] = [];
            for (const l of cssAdressen) {
              const pfad = l.href.startsWith('/') ? l.href : `${htmlDir}/${l.href}`;
              try {
                const res = await resilientFetch(`${API}/file?path=${encodeURIComponent(pfad)}${hausQ()}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const daten = await res.json();
                if (typeof daten?.content !== 'string') throw new Error('Antwort ohne content');
                teile.push(daten.content);
              } catch (err: any) {
                /* Fail loud: ein fehlendes Blatt wird nicht stillschweigend zu einer
                 * weissen Seite — die Vorschau sagt, welche Datei fehlt und warum. */
                teile.push(`/* ${l.href} nicht geladen: ${err?.message ?? err} */\n`
                  + `body::before{display:block;background:#f85149;color:#fff;padding:8px 12px;`
                  + `font:13px sans-serif;content:"Stylesheet ${l.href} konnte nicht geladen werden: `
                  + `${String(err?.message ?? err).replace(/"/g, "'")}"}`);
              }
            }
            setHtmlStile({ geladenFuer: selectedFile.path, css: teile.join('\n') });
            setHtmlStileLaden(false);
          })();
        }
        return (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'var(--tn-bg-dark)', color: 'var(--tn-text-muted)', fontSize: 13 }}>
            Stylesheet wird geladen…
          </div>
        );
      }

      // Uebrige relative Adressen (Bilder, Skripte) weiterhin ueber den API-Weg.
      let processedContent = content.replace(
        /(src|href)\s*=\s*"(?!https?:\/\/|data:|\/api\/|#|mailto:)([^"]+)"/g,
        (_match: string, attr: string, relPath: string) => {
          const absPath = `${htmlDir}/${relPath}`;
          return `${attr}="${API}/file?path=${encodeURIComponent(absPath)}${hausQ()}"`;
        }
      );
      // Die <link>-Zeilen durch den geholten Inhalt ersetzen (erste traegt alles).
      if (cssAdressen.length > 0 && htmlStile?.geladenFuer === selectedFile.path) {
        cssAdressen.forEach((l, i) => {
          const umgeschrieben = l.tag.replace(
            /href\s*=\s*"([^"]+)"/i,
            (_m, h) => `href="${API}/file?path=${encodeURIComponent(h.startsWith('/') ? h : `${htmlDir}/${h}`)}${hausQ()}"`
          );
          const ersatz = i === 0 ? `<style>\n${htmlStile.css}\n</style>` : '';
          processedContent = processedContent.replace(umgeschrieben, ersatz).replace(l.tag, ersatz);
        });
      }

      return (
        <iframe
          data-ai-id="file-preview-html-iframe"
          srcDoc={processedContent}
          style={{ flex: 1, border: 'none', width: '100%', background: 'white' }}
          sandbox="allow-scripts allow-same-origin"
        />
      );
    }

    if (ext === '.json') {
      let formatted = content;
      try { formatted = JSON.stringify(JSON.parse(content), null, 2); } catch {} // silent-ok: invalid JSON formatting attempt fails gracefully; raw content displayed
      return <CodeViewer content={formatted} ext=".json" fontSize={fontSize} onZoom={changeFontSize} />;
    }

    // Alle übrigen textbasierten Dateien (.py, .ts, .sh, .yaml, .sql, .log, …):
    // Syntax-Highlighting + Zeilennummern + Zoom, Sprache aus der Extension.
    return <CodeViewer content={content} ext={ext} fontSize={fontSize} onZoom={changeFontSize} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--tn-surface)' }}>
      {/* Path Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
        background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)',
        height: 30, flexShrink: 0,
      }}>
        {mode === 'browse' && (
          <>
            <button
              data-ai-id="file-preview-back"
              onClick={goBack}
              disabled={verlaufLaenge === 0}
              style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: verlaufLaenge === 0 ? 'default' : 'pointer', fontSize: 13, opacity: verlaufLaenge === 0 ? 0.35 : 1 }}
              title="Zurück zum vorigen Ordner / Back"
            >
              ←
            </button>
            <button
              data-ai-id="file-preview-up"
              onClick={goUp}
              disabled={!currentDir || currentDir === '/'}
              style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 13 }}
              title="Übergeordneter Ordner / Up"
            >
              ↑
            </button>
            <button
              data-ai-id="file-preview-home"
              onClick={goToDefault}
              disabled={!defaultDir || defaultDir === currentDir}
              style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: (!defaultDir || defaultDir === currentDir) ? 'default' : 'pointer', fontSize: 12, opacity: (!defaultDir || defaultDir === currentDir) ? 0.35 : 1 }}
              title={defaultDir ? `Zum Standard-Ordner / Home: ${defaultDir}` : 'Kein Standard-Ordner gesetzt'}
            >
              ⌂
            </button>
          </>
        )}
        {mode === 'browse' ? (
          <input
            data-ai-id="file-preview-path-input"
            value={inputPath}
            onChange={(e) => setInputPath(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(); }}
            placeholder="Pfad eingeben..."
            style={{
              flex: 1, background: 'var(--tn-bg)', color: 'var(--tn-text)',
              border: '1px solid var(--tn-border)', borderRadius: 4, padding: '2px 8px', fontSize: 11,
            }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 11, color: 'var(--tn-purple)', fontWeight: 600 }}>
            CLAUDE.md Navigator ({claudeTotal} files)
          </span>
        )}
        {mode === 'browse' && kannStandardMerken && (
          <button
            data-ai-id="file-preview-pin-default"
            onClick={pinCurrentDirAsDefault}
            disabled={!currentDir || currentDir === defaultDir}
            style={{
              background: defaultSaved ? 'rgba(158,206,106,0.2)' : 'none',
              border: '1px solid ' + (defaultSaved ? 'color-mix(in srgb, var(--tn-green) 50%, transparent)' : 'var(--tn-border)'),
              borderRadius: 3, padding: '1px 6px', fontSize: 10, flexShrink: 0,
              color: defaultSaved ? 'var(--tn-green)' : 'var(--tn-text-muted)',
              cursor: (!currentDir || currentDir === defaultDir) ? 'default' : 'pointer',
              opacity: (!currentDir || currentDir === defaultDir) ? 0.4 : 1,
            }}
            title={
              currentDir === defaultDir
                ? `Dieser Ordner ist bereits der Standard: ${defaultDir}`
                : `"${currentDir}" als Start-Ordner dieses Panels merken`
            }
          >
            {defaultSaved ? '✓ gemerkt' : '📌 Als Standard'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: '4px 8px', fontSize: 11, color: 'var(--tn-red)', background: 'var(--tn-bg-dark)' }}>
          {error}
        </div>
      )}

      {/* Quick Nav + Mode Toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 2, padding: '2px 8px',
        background: 'var(--tn-bg)', borderBottom: '1px solid var(--tn-border)',
        flexShrink: 0, flexWrap: 'wrap', minHeight: 22,
      }}>
        {/* CLAUDE Mode Toggle */}
        <button
          data-ai-id="file-preview-claude-mode-toggle"
          onClick={() => { setMode(m => m === 'claude' ? 'browse' : 'claude'); setSelectedFile(null); }}
          style={{
            background: mode === 'claude' ? 'rgba(187,154,247,0.2)' : 'none',
            border: mode === 'claude' ? '1px solid rgba(187,154,247,0.4)' : '1px solid transparent',
            borderRadius: 3, padding: '1px 6px', fontSize: 10,
            color: 'var(--tn-purple)', cursor: 'pointer',
            fontWeight: mode === 'claude' ? 700 : 600,
            marginRight: 4,
          }}
          title="CLAUDE.md Navigator"
        >
          CLAUDE
        </button>

        {/* Baum ein-/ausklappen */}
        <button
          data-ai-id="file-preview-tree-toggle"
          onClick={() => setBaumOffen(o => !o)}
          style={{
            background: baumOffen ? 'var(--tn-bg-highlight)' : 'none',
            border: '1px solid ' + (baumOffen ? 'var(--tn-border)' : 'transparent'),
            borderRadius: 3, padding: '1px 6px', fontSize: 10,
            color: 'var(--tn-text-muted)', cursor: 'pointer', marginRight: 4,
          }}
          title={baumOffen ? 'Dateiliste einklappen \u2014 mehr Platz zum Lesen' : 'Dateiliste zeigen'}
        >
          {baumOffen ? '\u25c0 Liste' : '\u25b6 Liste'}
        </button>

        {/* Separator */}
        <span style={{ width: 1, height: 12, background: 'var(--tn-border)', marginRight: 2 }} />

        {/* Quick Dir buttons (only in browse mode) */}
        {mode === 'browse' && QUICK_DIRS.map((d) => (
          <button
            key={d.path}
            data-ai-id={`file-preview-quickdir-${d.label}`}
            onClick={() => { setInputPath(d.path); loadDir(d.path); setSelectedFile(null); }}
            style={{
              background: currentDir.startsWith(d.path) ? 'var(--tn-bg-highlight)' : 'none',
              border: currentDir.startsWith(d.path) ? '1px solid var(--tn-border)' : '1px solid transparent',
              borderRadius: 3, padding: '1px 6px', fontSize: 10,
              color: d.color || 'var(--tn-text-muted)', cursor: 'pointer',
              fontWeight: currentDir === d.path ? 700 : 400,
            }}
            title={d.path}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Split: File List + Preview */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Sidebar — eingeklappt, wenn das Rezept die Datei schon benennt */}
        {baumOffen && (
        <div style={{ width: mode === 'claude' ? 240 : 260, borderRight: '1px solid var(--tn-border)', overflow: 'auto', flexShrink: 0 }}>
          {mode === 'claude' ? renderClaudeTree() : (
            <>
              {entries.map((e) => (
                <FileEntryRow
                  hausQ={hausQ()}
                  key={e.path}
                  entry={e}
                  selected={selectedFile?.path === e.path}
                  isAdmin={isAdmin}
                  onOpen={() => {
                    if (e.isDir) {
                      setInputPath(e.path);
                      loadDir(e.path);
                      setSelectedFile(null);
                    } else {
                      loadFile(e.path);
                    }
                  }}
                />
              ))}
              {entries.length === 0 && currentDir && (
                <div style={{ padding: 12, fontSize: 11, color: 'var(--tn-text-muted)', textAlign: 'center' }}>Leer</div>
              )}
            </>
          )}
        </div>
        )}

        {/* Content Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
          {selectedFile && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '2px 8px', fontSize: 10,
              borderBottom: '1px solid var(--tn-border)', background: 'var(--tn-bg)',
            }}>
              <span style={{ flex: 1, color: 'var(--tn-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedFile.path}
              </span>
              {/* Font Size Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginRight: 4 }}>
                <button
                  data-ai-id="file-preview-fontsize-decrease"
                  onClick={() => changeFontSize(-1)}
                  style={{
                    width: 18, height: 18, fontSize: 12, lineHeight: '16px',
                    background: 'none', border: '1px solid var(--tn-border)', borderRadius: 3,
                    color: 'var(--tn-text-muted)', cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  title="Schrift kleiner"
                >A</button>
                <span style={{ fontSize: 9, color: 'var(--tn-text-muted)', minWidth: 18, textAlign: 'center' }}>{fontSize}</span>
                <button
                  data-ai-id="file-preview-fontsize-increase"
                  onClick={() => changeFontSize(1)}
                  style={{
                    width: 18, height: 18, fontSize: 14, lineHeight: '16px', fontWeight: 700,
                    background: 'none', border: '1px solid var(--tn-border)', borderRadius: 3,
                    color: 'var(--tn-text-muted)', cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  title="Schrift groesser"
                >A</button>
              </div>
              {/* Edit-Button — nur bei textbasierten Files mit erlaubter Extension */}
              {(() => {
                const ext = (selectedFile.ext || '').toLowerCase();
                const editable = SAVABLE_EXTENSIONS.has(ext);
                if (!editable) return null;
                if (editingContent !== null) {
                  // Edit-Mode aktiv → Save + Discard buttons
                  const onSave = async () => {
                    setEditSaving(true);
                    setEditError('');
                    try {
                      const res = await resilientFetch(`${API}/file/save?${hausQ().slice(1)}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ path: selectedFile.path, content: editingContent }),
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({ error: 'unknown' }));
                        throw new Error(err.error || `HTTP ${res.status}`);
                      }
                      // Erfolg → Reload Datei + Edit-Mode beenden
                      setEditingContent(null);
                      await loadFile(selectedFile.path);
                    } catch (err: any) {
                      setEditError(err.message || 'Save failed');
                    } finally {
                      setEditSaving(false);
                    }
                  };
                  const onDiscard = () => {
                    if (editingContent !== selectedFile.content) {
                      if (!confirm('Änderungen verwerfen?')) return;
                    }
                    setEditingContent(null);
                    setEditError('');
                  };
                  return (
                    <>
                      <button
                        data-ai-id="file-preview-edit-save"
                        onClick={onSave}
                        disabled={editSaving}
                        style={{
                          padding: '2px 8px', fontSize: 10, borderRadius: 3,
                          background: 'var(--tn-green)', color: 'white', border: 'none',
                          cursor: editSaving ? 'wait' : 'pointer', opacity: editSaving ? 0.6 : 1,
                          fontWeight: 600,
                        }}
                        title="Speichern"
                      >
                        {editSaving ? 'Speichert…' : 'Speichern'}
                      </button>
                      <button
                        data-ai-id="file-preview-edit-discard"
                        onClick={onDiscard}
                        disabled={editSaving}
                        style={{
                          padding: '2px 8px', fontSize: 10, borderRadius: 3,
                          background: 'transparent', color: 'var(--tn-text-muted)',
                          border: '1px solid var(--tn-border)', cursor: 'pointer',
                        }}
                        title="Änderungen verwerfen"
                      >
                        Abbrechen
                      </button>
                    </>
                  );
                }
                // Kein Edit-Mode → Bearbeiten-Button
                return (
                  <button
                    data-ai-id="file-preview-edit-start"
                    onClick={() => {
                      setEditingContent(selectedFile.content);
                      setEditError('');
                    }}
                    style={{
                      padding: '2px 8px', fontSize: 10, borderRadius: 3,
                      background: 'var(--tn-blue)', color: 'white', border: 'none',
                      cursor: 'pointer',
                    }}
                    title="Datei im Source-Editor bearbeiten"
                  >
                    Bearbeiten
                  </button>
                );
              })()}
              <a
                data-ai-id="file-preview-download"
                href={`${API}/file/download?path=${encodeURIComponent(selectedFile.path)}${hausQ()}`}
                download
                style={{
                  padding: '2px 8px', fontSize: 10, borderRadius: 3,
                  background: 'var(--tn-green)',
                  color: 'white', border: 'none', cursor: 'pointer',
                  textDecoration: 'none', display: 'inline-block',
                }}
                title="Download file"
              >
                Download
              </a>
              {stageDir && (
                <button
                  data-ai-id="file-preview-stage"
                  onClick={stageFile}
                  disabled={stageLoading}
                  style={{
                    padding: '2px 8px', fontSize: 10, borderRadius: 3,
                    background: stageSuccess ? 'var(--tn-green)' : 'var(--tn-blue)',
                    color: 'white', border: 'none', cursor: stageLoading ? 'wait' : 'pointer',
                    opacity: stageLoading ? 0.6 : 1,
                  }}
                  title={`Copy to ${stageDir}`}
                >
                  {stageLoading ? '...' : stageSuccess ? 'Staged' : 'Stage'}
                </button>
              )}
            </div>
          )}
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

/**
 * Sidebar-Zeile für einen Baum-Eintrag (Datei oder Ordner). Eigener Row-Wrapper
 * (statt geschachteltem <button>) analog BusinessDocsPanel.DocItem — Klick öffnet
 * die Zeile, der Download-Link ist ein separates, per Hover eingeblendetes Element
 * damit er nicht (invalides HTML) in einem <button> verschachtelt liegt.
 */
function FileEntryRow({ entry, selected, isAdmin, onOpen, hausQ = '' }: {
  /** `&haus=…`, wenn der Baum ein anderes Haus zeigt (siehe FilePreview `haus`). */
  hausQ?: string;
  entry: FileEntry;
  selected: boolean;
  isAdmin: boolean;
  onOpen: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const downloadHref = entry.isDir
    ? `${API}/files/download-zip?path=${encodeURIComponent(entry.path)}${hausQ}`
    : `${API}/file/download?path=${encodeURIComponent(entry.path)}${hausQ}`;

  return (
    <div
      data-ai-id={`file-preview-file-entry-${entry.name}`}
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={entry.name}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 6, width: '100%',
        padding: '3px 8px',
        background: selected ? 'var(--tn-bg-highlight)' : 'transparent',
        color: entry.isDir ? 'var(--tn-blue)' : 'var(--tn-text)',
        fontSize: 11, textAlign: 'left', cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: 10, opacity: 0.6, flexShrink: 0, marginTop: 1 }}>{entry.isDir ? '/' : getFileIcon(entry.ext ?? null)}</span>
      <span style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.35, flex: 1 }}>{entry.name}</span>
      {isAdmin && hovered && (
        <a
          data-ai-id={`file-preview-entry-download-${entry.name}`}
          href={downloadHref}
          download
          onClick={(ev) => ev.stopPropagation()}
          title={entry.isDir ? 'Ordner als ZIP herunterladen' : 'Datei herunterladen'}
          style={{
            flexShrink: 0, fontSize: 10, color: 'var(--tn-text-muted)',
            textDecoration: 'none', padding: '0 2px', lineHeight: '14px',
          }}
        >
          ⬇
        </a>
      )}
    </div>
  );
}

function getFileIcon(ext: string | null): string {
  switch (ext) {
    case '.md': return 'M';
    case '.json': return 'J';
    case '.ts': case '.tsx': return 'T';
    case '.js': case '.jsx': return 'J';
    case '.py': return 'P';
    case '.html': return 'H';
    case '.css': return 'C';
    case '.pdf': return 'P';
    case '.mmd': case '.merm': return 'D';
    case '.png': case '.jpg': case '.jpeg': case '.gif': case '.svg': return 'I';
    default: return '-';
  }
}
